import { useEffect, useState } from "react";

import type { OdsOrganisationDetails } from "../api.js";
import { api } from "../api.js";

interface UseOdsOrganisationResult {
  organisation: OdsOrganisationDetails | null;
  loading: boolean;
  error: boolean;
}

/**
 * Look up an NHS organisation (e.g. GP practice) by ODS code via the
 * server-side ODS proxy. The frontend never talks to the NHS ODS API
 * directly so the API key + endpoint stay on the server.
 */
export function useOdsOrganisation(
  odsCode: string | null | undefined,
): UseOdsOrganisationResult {
  const [organisation, setOrganisation] =
    useState<OdsOrganisationDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!odsCode) {
      setOrganisation(null);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setOrganisation(null);
    setLoading(true);
    setError(false);

    api.ods
      .getOrganisation(odsCode)
      .then((response) => {
        if (cancelled) return;
        if (response.status === 200) {
          setOrganisation(response.data);
        } else if (response.status === 404) {
          setOrganisation(null);
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [odsCode]);

  return { organisation, loading, error };
}

/**
 * Batch variant of {@link useOdsOrganisation}: resolves a set of ODS codes to
 * organisation names in one place, **de-duplicating** so each distinct code is
 * fetched only once. RAF records typically repeat the same organisation code
 * across every adjustment/condition, so a per-item lookup would fire many
 * identical requests.
 *
 * Returns a `code -> name` map containing only the codes that resolved to a
 * known organisation; callers fall back to the raw code for anything missing.
 */
export function useOdsOrganisations(
  codes: readonly (string | null | undefined)[],
): Record<string, string> {
  // A stable dependency: the sorted, de-duplicated, non-empty codes. Joining to
  // a string avoids re-running the effect when only the array identity changes.
  const uniqueKey = [...new Set(codes.filter((c): c is string => Boolean(c)))]
    .sort()
    .join("|");

  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const uniqueCodes = uniqueKey ? uniqueKey.split("|") : [];
    if (uniqueCodes.length === 0) {
      setNames({});
      return;
    }

    let cancelled = false;

    void Promise.all(
      uniqueCodes.map(async (code) => {
        try {
          const response = await api.ods.getOrganisation(code);
          if (response.status === 200 && response.data) {
            return [code, response.data.name] as const;
          }
        } catch {
          // Ignore — the call site falls back to the raw code.
        }
        return null;
      }),
    ).then((entries) => {
      if (cancelled) return;
      const resolved: Record<string, string> = {};
      for (const entry of entries) {
        if (entry) resolved[entry[0]] = entry[1];
      }
      setNames(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [uniqueKey]);

  return names;
}
